import { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { functionalUpdate, SortingState } from '@tanstack/react-table';
import { Skeleton, tableHelpers, VirtualizedTable } from '@round/creatorbase-ui';
import { formatDate } from 'helpers';
import { OutreachStatusBadge } from '../../../components/OutreachStatusBadge/OutreachStatusBadge';
import { FiltersCell } from './cells/FiltersCell/FiltersCell';
import { OutputsCell } from './cells/OutputsCell/OutputsCell';
import { TargetsCell } from './cells/TargetsCell/TargetsCell';
import { Column, getCountValue, getMeta, Meta, NO_VALUE, PAGE_SIZE, Row } from './helpers';
import styles from './OutreachRequestsTable.module.css';

const SkeletonCell = tableHelpers.SkeletonCell;
const TruncatedCell = tableHelpers.TruncatedCell;

const ROW_HEIGHT_PX = 50;

// The table pins a cell's min-width to the column's `size`, so the class below is what turns that
// size into a minimum and shares the spare width out from there.
const COLUMN = { className: styles.column };
const WIDE_COLUMN = { className: styles.wideColumn };

type Props = {
    rows: Row[];
    meta: Meta;
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    fetchNextPage: () => void;
    /** Why the table has no rows is the page's to say, so the empty state comes in from outside. */
    noDataLabel: ReactNode;
};

export const OutreachRequestsTable = ({ rows, meta, sorting, onSortingChange, fetchNextPage, noDataLabel }: Props) => {
    const navigate = useNavigate();

    const columns: Column[] = useMemo(
        () => [
            {
                header: 'Name',
                id: 'name',
                accessorFn: (row) => row.name,
                cell: TruncatedCell,
                meta: WIDE_COLUMN,
                size: 280,
                enableSorting: false,
            },
            {
                header: 'Status',
                id: 'status',
                enableSorting: false,
                cell: ({ row: { original }, table }) => {
                    const { isLoading } = getMeta(table);

                    if (tableHelpers.isLoadingRow(original) || isLoading) {
                        return <Skeleton className={styles.statusSkeleton} />;
                    }

                    return <OutreachStatusBadge status={original.status} />;
                },
                meta: COLUMN,
                size: 180,
            },
            {
                header: 'Targets',
                id: 'targets',
                enableSorting: false,
                cell: TargetsCell,
                meta: WIDE_COLUMN,
                size: 200,
            },
            {
                header: 'Filters',
                id: 'filters',
                enableSorting: false,
                cell: FiltersCell,
                meta: WIDE_COLUMN,
                size: 240,
            },
            {
                header: 'Outputs',
                id: 'outputs',
                enableSorting: false,
                cell: OutputsCell,
                meta: WIDE_COLUMN,
                size: 100,
            },
            {
                header: 'Results',
                id: 'results_count',
                enableSorting: false,
                accessorFn: (row) => getCountValue(row.results_count),
                cell: SkeletonCell,
                meta: COLUMN,
                size: 110,
            },
            {
                header: 'To review',
                id: 'to_review_count',
                enableSorting: false,
                accessorFn: (row) => getCountValue(row.to_review_count),
                cell: SkeletonCell,
                meta: COLUMN,
                size: 110,
            },
            {
                header: 'Archived',
                id: 'archived_count',
                enableSorting: false,
                accessorFn: (row) => getCountValue(row.archived_count),
                cell: SkeletonCell,
                meta: COLUMN,
                size: 110,
            },
            {
                header: 'Added',
                id: 'added_count',
                enableSorting: false,
                accessorFn: (row) => getCountValue(row.added_count),
                cell: SkeletonCell,
                meta: COLUMN,
                size: 110,
            },
            {
                header: 'Deadline',
                id: 'deadline',
                accessorFn: (row) => (row.deadline ? formatDate(row.deadline) : NO_VALUE),
                cell: SkeletonCell,
                meta: COLUMN,
                size: 140,
            },
            {
                header: 'Created',
                id: 'created_at',
                accessorFn: (row) => (row.created_at ? formatDate(row.created_at) : NO_VALUE),
                cell: SkeletonCell,
                meta: COLUMN,
                size: 140,
            },
        ],
        []
    );

    const data = tableHelpers.getLoadingRows(rows, { pageSize: PAGE_SIZE, isLoading: meta.isLoading });

    return (
        <VirtualizedTable
            columns={columns}
            data={data}
            meta={meta}
            virtualizerOptions={{
                estimateSize: () => ROW_HEIGHT_PX,
                getItemKey: (index) => data[index]?.id ?? index,
            }}
            onEndReached={fetchNextPage}
            // The endpoint orders the rows, so the table only reports the intent.
            enableSorting
            manualSorting
            // One ordering value goes on the wire, so a shift-click must not stack a second sort.
            enableMultiSort={false}
            sorting={sorting}
            onSortingChange={(newSorting) => onSortingChange(functionalUpdate(newSorting, sorting))}
            containerClassName={styles.tableContainer}
            onRowClick={(row) => {
                if (!tableHelpers.isLoadingRow(row.original)) {
                    navigate(`${row.original.id}/`);
                }
            }}
            noDataLabel={noDataLabel}
        />
    );
};
